import axios from "axios";
import { User as firebaseAuthUser } from "firebase/auth";
import { createSearchParams, matchRoutes, NavigateFunction } from "react-router-dom";
import { APPS, MAX_INC_RESOLUTION_PIXELS } from "../constants/AppsConstants.ts";
import { LocalStorageConstants } from "../constants/LocalStorageConstants.ts";

import { BRIA_QUALCOMM_SERVER_HOST } from "../constants/common.ts";
import {
	ACCENTURE_ORG_ID,
	AMANA_ORG_ID,
	APRIMO_ORG_ID,
	ASAF_HANUKA_ORG_ID,
	AWS_ORG_ID,
	BRIA_DEMOS_ORG_ID,
	BRIA_ORG_ID,
	BRIA_PUBLIC_ORG_ID,
	FOX_ORG_ID,
	LOBLAW_ORG_ID,
	MCCANN_ORG_ID,
	MIROGLIO_ORG_ID,
	NEXAR_ORG_ID,
	NVIDIA_GAMING_ORG_ID,
	PAMPERS_ORG_ID,
	PUBLICIS_LE_PONT_UNILEVER_ORG_ID,
	PUBLICIS_ORG_ID,
	PURINA_ORG_ID,
	RAKUTEN_ORG_ID,
	SAFFRON_ORG_ID,
	TAG_WORLDWIDE_ORG_ID,
	TOMER_HANUKA_ORG_ID,
	TRAILER_PARK_GROUP_ORG_ID,
	VML_ORG_ID,
	WPP_POC_ORG_ID,
} from "../constants/OrgConstants.ts";
import RouterConstants from "../constants/RouterConstants.ts";
import { USER_ORGANIZATION_ROLES } from "../constants/UserConstants.ts";
import { getSelectedOrganization } from "../helpers/localStorage.ts";
import { PlaygroundImage, PlaygroundResult } from "../models/playground.ts";
import { TextToImageConfigType } from "../models/text-to-image.ts";
import User from "../models/user.ts";
import { convertS3UriToHttpsUrl } from "../pages/Campaign/Brands/utils.tsx";

export function capitalizeFirstLetter(text: string) {
	return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

export const getOrganizationAdmins = (user: any) => {
	let adminsList = user.user_organizations?.filter((item: any) => {
		return (
			item.role === USER_ORGANIZATION_ROLES.ADMIN ||
			item.role === USER_ORGANIZATION_ROLES.ADMIN_HIDDEN ||
			item.role === USER_ORGANIZATION_ROLES.OWNER
		);
	});
	adminsList = adminsList?.map((item: any) => item.org_uid);
	return adminsList ?? [];
};

export const isBriaAdmin = (user: User | null) => {
	return (
		user?.user_organizations?.some(
			(item: { org_uid: string; status: string; role: string }) =>
				item.org_uid === BRIA_ORG_ID && item.role === USER_ORGANIZATION_ROLES.ADMIN,
		) ?? false
	);
};

export const isBriaMember = (user: User | null) => {
	return (
		user?.user_organizations?.some(
			(item: { org_uid: string; status: string; role: string }) => item.org_uid === BRIA_ORG_ID,
		) ?? false
	);
};

export const isNvidiaGaminOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === NVIDIA_GAMING_ORG_ID;
};

export const validateEmail = (email: string) => {
	return String(email)
		.toLowerCase()
		.match(
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
		);
};

export const isPublicisOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === PUBLICIS_ORG_ID;
};

export const isPublicisLePointUnileverOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === PUBLICIS_LE_PONT_UNILEVER_ORG_ID;
};

export const isAmanaOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === AMANA_ORG_ID;
};

export const isBriaOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === BRIA_ORG_ID;
};

export const isAwsOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === AWS_ORG_ID;
};

export const isLoblawOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === LOBLAW_ORG_ID;
};

export const isAprimoOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === APRIMO_ORG_ID;
};

export const isRakutenOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === RAKUTEN_ORG_ID;
};

export const isVmlOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === VML_ORG_ID;
};

export const isMiroglioOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === MIROGLIO_ORG_ID;
};

export const isNexarOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === NEXAR_ORG_ID;
};

export const isSaffronOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === SAFFRON_ORG_ID;
};

export const isTrailerParkGroupOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === TRAILER_PARK_GROUP_ORG_ID;
};

export const isMccannOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === MCCANN_ORG_ID;
};

export const isBriaDemosOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === BRIA_DEMOS_ORG_ID;
};

export const isWppPocOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === WPP_POC_ORG_ID;
};

export const isFoxOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === FOX_ORG_ID;
};

export const isPurinaOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === PURINA_ORG_ID;
};

export const isPampersOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === PAMPERS_ORG_ID;
};

export const isAccentureOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === ACCENTURE_ORG_ID;
};

export const isHanukaOrgs = () => {
	const selectedOrg = getSelectedOrganization();
	return (
		selectedOrg?.organization?.uid === ASAF_HANUKA_ORG_ID || selectedOrg?.organization?.uid === TOMER_HANUKA_ORG_ID
	);
};

export const isTagWorldwideOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === TAG_WORLDWIDE_ORG_ID;
};

export const isBriaPublicOrg = () => {
	const selectedOrg = getSelectedOrganization();
	return selectedOrg?.organization?.uid === BRIA_PUBLIC_ORG_ID;
};

export const navigateWithParams = (navigate: NavigateFunction, path: string) => {
	const queryParams = new URLSearchParams(location.search);
	navigate(`${path}?${queryParams.toString()}`);
};

export function formatDate(apiDateString?: Date | string): string {
	if (apiDateString) {
		const apiDate = new Date(apiDateString);
		const options: Intl.DateTimeFormatOptions = {
			day: "numeric",
			month: "short",
			year: "numeric",
		};
		return apiDate.toLocaleDateString(undefined, options);
	} else {
		return "";
	}
}

export const isModelTypeDropDownVisible = () => {
	return false;
};

export const getSafeFileName = (filename: string) => {
	return filename?.replace(/[^a-z0-9]/gi, "_");
};

export const getFileExtensionFromUrl = (url: string, fallback = "png"): string => {
	const pathname = (() => {
		try {
			return new URL(url).pathname;
		} catch {
			return url;
		}
	})();
	const match = pathname.match(/\.([a-z0-9]+)$/i);
	return match ? match[1] : fallback;
};

export const hasOrganization = () => {
	const selectedOrg = getSelectedOrganization();
	return !!selectedOrg?.organization;
};

export const getRouteConfigs = (url: string) => {
	const routes = Object.values(RouterConstants).map((config) => ({
		path: config.fullPath ?? config.path,
	}));

	const matchedRoutes = matchRoutes(routes, url);

	if (matchedRoutes && matchedRoutes.length > 0) {
		const match = matchedRoutes[0];
		return Object.values(RouterConstants).find((config) => config.fullPath === match.route.path);
	}

	return null;
};

export const getTodayDateRange = (): [Date, Date] => {
	const now = new Date();
	const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
	const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	return [startDate, endDate];
};

export const convertDateToUtc = (date: Date) => {
	return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
};

export const removeParamFromLocationUrl = (params: string[], navigate?: NavigateFunction) => {
	const queryParams = new URLSearchParams(location.search);
	params.forEach((param) => queryParams.delete(param));
	if (navigate) {
		navigate(
			{
				search: createSearchParams(queryParams).toString(),
			},
			{ replace: true },
		);
	}
	return `${location.origin}${location.pathname}?${queryParams.toString()}`;
};

export const loadFont = (fontSrc: any) => {
	const style = document.createElement("style");
	const httpUrl = convertS3UriToHttpsUrl(fontSrc.src);
	style.appendChild(
		document.createTextNode(`
	  @font-face {
		font-family: "${fontSrc.label ?? fontSrc.name}";
		src: url("${httpUrl}") format("truetype");
	  }
	`),
	);
	document.head.appendChild(style);
};

export const sortById = (list: any[] | undefined) => {
	return list ? list.slice().sort((itemA: any, itemB: any) => itemA.id - itemB.id) : [];
};
export const isFoxApps = () => {
	return window.location.pathname.includes("fox-apps");
};

export const isCanvasRequiredForThisApp = () => {
	const appsWithCanvas = ["eraser", "gen-fill"];
	return appsWithCanvas.some((app) => window.location.pathname.includes(app));
};

export const isAppsURL = () => {
	return window.location.pathname.includes("apps");
};

export const isPlatformHubPage = () => {
	return window.location.pathname === "/apps";
};

export const convertToCamelCase = (str: string): string => {
	if (str.trim().length === 0) return str;

	const parts: string[] = str.split("_");
	let camelCase: string = parts[0].toLowerCase();

	for (let i = 1; i < parts.length; i++) {
		if (parts[i]) {
			camelCase += parts[i].charAt(0).toUpperCase() + parts[i].slice(1).toLowerCase();
		}
	}

	return camelCase;
};

export const urlToFile = async (url: string, filename: string) => {
	const response = await fetch(url);

	if (!response.ok) {
		throw new Error("Failed to fetch image");
	}

	const mimeType = response.headers.get("Content-Type") || "application/octet-stream";

	const blob = await response.blob();

	return new File([blob], filename, { type: mimeType });
};

export const createFileList = (files: File[]): FileList => {
	const dataTransfer = new DataTransfer();
	files.forEach((file) => dataTransfer.items.add(file));
	return dataTransfer.files;
};

export const truncateToTwoDecimals = (num: number) => {
	return Math.floor(num * 100) / 100;
};

export const isPlaygroundLoadingImages = (playgroundResults: PlaygroundResult[], type: string) => {
	const lastIndex = playgroundResults.reduce(
		(lastIndex, item, index) => (item.type === type ? index : lastIndex),
		-1,
	);

	if (lastIndex === -1) return null;

	const playgroundResult = playgroundResults[lastIndex];

	const hasLoadingImages = playgroundResult?.images?.some((image) => image.loading);

	return hasLoadingImages ? playgroundResult : null;
};

export const getUsedModelsNames = (selectedImages: PlaygroundImage[], playgroundResult: PlaygroundResult[]): string => {
	const extractModelName = (config: unknown): string | null => {
		return (config as TextToImageConfigType)?.model_name || null;
	};

	const getValidModelNames = (items: PlaygroundImage[]): string[] => {
		return items.map((item) => extractModelName(item.config)).filter((name): name is string => name !== null);
	};

	const modelNames =
		selectedImages.length > 0
			? getValidModelNames(selectedImages)
			: playgroundResult
					.filter((item) => item?.type === APPS.TEXT_TO_IMAGE)
					.map((item) => extractModelName(item.config))
					.filter((name): name is string => name !== null);

	return Array.from(new Set(modelNames)).join(", ");
};

export const checkImageResolution = (url: string, scaleFactor: number = 1) => {
	return new Promise<boolean>((resolve) => {
		const img = new Image();
		img.src = url;
		img.onload = () => {
			const resolution = img.width * img.height * Math.pow(scaleFactor, 2);
			resolve(resolution > MAX_INC_RESOLUTION_PIXELS);
		};
		// The resolution can't be determined, so don't restrict any resolution options
		img.onerror = () => {
			console.warn("Failed to load image for resolution check", url);
			resolve(false);
		};
	});
};

export const campaignEditorPaths = [
	RouterConstants.ADS_EDITOR.path,
	RouterConstants.ADS_GENERATOR.path,
	RouterConstants.ADS_GENERATION.DEMO.BRANDS_TEMPLATES_EDITOR.path,
	RouterConstants.ADS_GENERATION.DEMO.ADS_EDITOR.path,
	RouterConstants.ADS_GENERATION.DEMO.ADS_GENERATION.path,
];

export const isCampaignEditor = campaignEditorPaths.some((path) => window.location.pathname.includes(path));

export const adsEditorPaths = [RouterConstants.ADS_EDITOR.path, RouterConstants.ADS_GENERATION.DEMO.ADS_EDITOR.path];

export const isAdsEditor = () => adsEditorPaths.some((path) => window.location.pathname.includes(path));

export const adsGeneratorPaths = [
	RouterConstants.ADS_GENERATOR.path,
	RouterConstants.ADS_GENERATION.DEMO.ADS_GENERATION.path,
];

export const isAdsGenerator = () => adsGeneratorPaths.some((path) => window.location.pathname.includes(path));

export const isAutomotiveApp = () => {
	return window.location.pathname.includes(RouterConstants.AUTOMOTIVE.path);
};

export const isAutomotiveUploadedPage = () => {
	return window.location.pathname.includes(RouterConstants.AUTOMOTIVE_PROJECT_UPLOADED.path);
};

export const isTextToImageApp = () => {
	return window.location.pathname.includes(RouterConstants.TEXT_TO_IMAGE.path);
};

export const isTGApp = () => {
	return window.location.pathname.includes(RouterConstants.NEW_TG.path);
};
export const isAutomotiveFavoritesPage = () => {
	return window.location.pathname.includes(RouterConstants.AUTOMOTIVE_PROJECT_FAVORITES.path);
};

const isLocalQualcommServerAvailable = async () => {
	let isAvailable = false;
	try {
		const resp = await axios.get(`${BRIA_QUALCOMM_SERVER_HOST}/health_check`, { timeout: 5000 });
		if (resp.status === 200) {
			isAvailable = true;
		}
	} catch (error: any) {
		console.log("error in health check call", error.message);
	}
	return isAvailable;
};

export const getLocalQualcommAvailableModels = async () => {
	let modelsList: string[] = [];
	try {
		if (await isLocalQualcommServerAvailable()) {
			const resp = await axios.get(`${BRIA_QUALCOMM_SERVER_HOST}/list_models`, { timeout: 5000 });
			if (resp.status === 200) {
				modelsList = resp.data;
			}
		}
	} catch (error: any) {
		console.log("error in list_models call", error.message);
	}
	return modelsList;
};

export const checkAwsMarketplaceToken = (): void => {
	const queryParams = new URLSearchParams(location.search);
	const awsRegistrationToken = queryParams.get(LocalStorageConstants.AWS_REGISTRATION_TOKEN) || undefined;
	if (awsRegistrationToken) {
		localStorage.setItem(LocalStorageConstants.AWS_REGISTRATION_TOKEN, awsRegistrationToken);
	}
};

export const formatScientific = (value: number | undefined | null): string => {
	if (!value && value !== 0) return "";
	const [mantissa, exp] = value.toExponential(6).split("e");
	return `${parseFloat(mantissa)}e${+exp}`;
};

export const isUserCreatedAfterDate = (firebaseUser: firebaseAuthUser | null, date: Date): boolean => {
	if (!firebaseUser?.metadata?.creationTime) {
		return false;
	}
	const userCreationTime = new Date(firebaseUser.metadata.creationTime).getTime();
	return userCreationTime > date.getTime();
};

export const isTextToVector = () => {
	return window.location.pathname.endsWith(RouterConstants.TEXT_TO_VECTOR.path);
};

export const isFiboApp = () => {
	return (
		window.location.pathname === RouterConstants.LABS.FIBO.fullPath ||
		window.location.pathname === RouterConstants.LABS.FIBO_BBQ.fullPath ||
		window.location.pathname === RouterConstants.LABS.FIBO_2.fullPath ||
		window.location.pathname === RouterConstants.LABS.FIBO_LITE.fullPath
	);
};

export const isFiboBbqApp = () => {
	return window.location.pathname === RouterConstants.LABS.FIBO_BBQ.fullPath;
};

export const isFibo2App = () => {
	return window.location.pathname === RouterConstants.LABS.FIBO_2.fullPath;
};

export const getFiboLabVariant = (): "FIBO" | "FIBO-BBQ" | "FIBO-2" | "FIBO-LITE" | null => {
	const pathname = window.location.pathname;
	if (pathname === RouterConstants.LABS.FIBO.fullPath) {
		return "FIBO";
	}
	if (pathname === RouterConstants.LABS.FIBO_BBQ.fullPath) {
		return "FIBO-BBQ";
	}
	if (pathname === RouterConstants.LABS.FIBO_2.fullPath) {
		return "FIBO-2";
	}
	if (pathname === RouterConstants.LABS.FIBO_LITE.fullPath) {
		return "FIBO-LITE";
	}
	return null;
};
/**
 * Checks if a date is within a certain number of months from a start date
 * @param {Date} dateToCheck - The date to check (usually today)
 * @param {Date} startDate - The reference start date
 * @param {number} months - Number of months from the start date
 * @returns {boolean} - true if dateToCheck is within the period
 */
export function isWithinMonths(dateToCheck: Date, startDate: Date, months: number = 1): boolean {
	const start = new Date(startDate);
	const end = new Date(startDate);
	end.setMonth(end.getMonth() + months);

	return dateToCheck >= start && dateToCheck <= end;
}

// Playground and demo routes
export const ALL_PLAYGROUND_ROUTES: string[] = [
	// Console demos
	RouterConstants.IMAGE_GENERATION.DEMO.IMAGE_GENERATION.fullPath,
	RouterConstants.IMAGE_EDITING.IMAGE.DEMO.fullPath,
	// Platform hub playgrounds
	RouterConstants.TEXT_TO_IMAGE.fullPath,
	RouterConstants.IMAGE_TO_IMAGE.fullPath,
];

export const isPlaygroundRoute = () => {
	const p = window.location.pathname;
	return ALL_PLAYGROUND_ROUTES.some((route) => p === route);
};

export function formatOrgNameForDisplay(raw: string): string {
	const sanitized = (raw || "").replace(/[^a-zA-Z0-9\s\-.&']/g, "").trim();

	const match = sanitized.match(/^(.+)'s Organization$/);

	let result = sanitized;

	if (match) {
		const firstName = match[1].length > 35 ? match[1].slice(0, 35) + "…" : match[1];
		result = `${firstName}'s Organization`;
	}

	if (result.length > 50) {
		return result.slice(0, 49) + "…";
	}

	return result;
}
